import { Suspense, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AppLayout, LoadingSpinner } from './Layout';

/**
 * One front door for work that belongs together.
 *
 * The app had thirteen menu items answering six questions, three separate places to find out
 * "what do I do next", two places to find out "where do my skills stand". This puts the existing
 * pages behind one set of tabs without rewriting a line inside them: the layout notices it is
 * nested and steps aside, so each page renders exactly as it always did.
 *
 * The tab lives in the URL (?tab=), so a link, a back button and a refresh all land in the
 * same place.
 */
export const TabbedHub = ({ tabs, testid = 'tabbed-hub' }) => {
  const [params, setParams] = useSearchParams();
  const requested = params.get('tab');
  const active = useMemo(
    () => tabs.find(t => t.key === requested) || tabs[0],
    [tabs, requested]
  );

  useEffect(() => {
    if (requested && !tabs.some(t => t.key === requested)) {
      setParams({}, { replace: true });
    }
  }, [requested, tabs, setParams]);

  const go = (key) => setParams(key === tabs[0].key ? {} : { tab: key }, { replace: false });

  return (
    <AppLayout>
      <div className="flex flex-wrap items-center gap-1.5 mb-6" data-testid={testid}>
        {tabs.map(t => {
          const on = t.key === active.key;
          return (
            <button key={t.key} onClick={() => go(t.key)} data-testid={`hub-tab-${t.key}`}
              className={`relative px-4 py-2 rounded-full text-sm transition-colors ${
                on ? 'text-slate-950 font-semibold' : 'text-slate-300 hover:text-white'}`}>
              {on && <span className="absolute inset-0 rounded-full bg-cyan-300" aria-hidden />}
              {!on && <span className="absolute inset-0 rounded-full border border-slate-700 hover:border-slate-500 transition-colors" aria-hidden />}
              <span className="relative">{t.label}</span>
            </button>
          );
        })}
      </div>
      <Suspense fallback={<div className="py-24 flex justify-center"><LoadingSpinner /></div>}>
        <div key={active.key}>{active.render()}</div>
      </Suspense>
    </AppLayout>
  );
};

export default TabbedHub;
